// ==========================================================================
// components/SuggestBudgets.tsx — "Suggest budgets" (9 Oct 2026)
//   Works out each spending code's average month from reconciled history and
//   offers it as the budget, rounded up to the next $10. Tick what to keep;
//   nothing changes until "Set ticked budgets". Months before the first
//   import don't count (they'd drag the average down to nothing).
// ==========================================================================
import { useMemo, useState } from "react";
import { addMonths, format, startOfMonth } from "date-fns";
import { Sparkle } from "lucide-react";
import { Button, ErrorText } from "@/components/ui";
import Sheet from "@/components/Sheet";
import { money, useCategories, useSaveCategory, type Category } from "@/lib/money";
import { useMonthTotals } from "@/lib/contacts";

const BASES = [3, 6, 12] as const;
const ymd = (d: Date) => format(d, "yyyy-MM-dd");

export default function SuggestBudgets({ onClose }: { onClose: () => void }) {
  const [basis, setBasis] = useState<(typeof BASES)[number]>(3);
  // Whole months only: the current month is still filling up.
  const end = startOfMonth(new Date()); const start = startOfMonth(addMonths(end, -basis));
  const totals = useMonthTotals(ymd(start), ymd(new Date(end.getTime() - 86400000)));
  const cats = useCategories(); const save = useSaveCategory();
  const all = cats.data ?? [];
  const byId = useMemo(() => new Map(all.map((c) => [c.id, c])), [all]);
  const groupOf = (c?: Category) => (c?.is_group ? c : c?.parent_id ? byId.get(c.parent_id) : undefined);

  const rows = useMemo(() => {
    const data = totals.data ?? [];
    const months = new Set(data.map((r) => r.month));   // months that have any coded lines
    const n = Math.max(1, months.size);
    const sum = new Map<string, number>();
    for (const r of data) {
      const c = byId.get(r.category_id); if (!c || c.is_group || c.archived_at || groupOf(c)?.kind !== "expense") continue;
      sum.set(c.id, (sum.get(c.id) ?? 0) - r.amount_cents);
    }
    return [...sum.entries()].map(([id, cents]) => {
      const c = byId.get(id)!; const avg = Math.max(0, cents / n);
      return { c, avg, suggested: Math.ceil(avg / 1000) * 1000 };   // up to the next $10
    }).filter((r) => r.suggested > 0).sort((a, b) => a.c.code.localeCompare(b.c.code));
  }, [totals.data, byId]); // eslint-disable-line react-hooks/exhaustive-deps
  const monthsUsed = new Set((totals.data ?? []).map((r) => r.month)).size;

  // Ticked by default: codes with no budget yet.
  const [ticked, setTicked] = useState<Set<string> | null>(null);
  const tick = ticked ?? new Set(rows.filter((r) => !r.c.monthly_budget_cents).map((r) => r.c.id));
  const [edits, setEdits] = useState<Record<string, string>>({});
  const value = (r: (typeof rows)[number]) => edits[r.c.id] ?? String(r.suggested / 100);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const chosen = rows.filter((r) => tick.has(r.c.id));
  const total = chosen.reduce((s, r) => s + Math.round(Number(value(r) || 0) * 100), 0);

  async function apply() {
    setBusy(true); setErr("");
    try {
      for (const r of chosen) {
        const cents = Math.round(Number(value(r) || 0) * 100);
        await save.mutateAsync({ id: r.c.id, c: { monthly_budget_cents: cents > 0 ? cents : null } });
      }
      onClose();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <Sheet wide title="Suggest budgets" onClose={onClose}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-ink-muted">Average of the last</span>
          <div className="flex rounded-full bg-surface-raised p-1 text-sm">
            {BASES.map((b) => <button key={b} type="button" onClick={() => { setBasis(b); setTicked(null); setEdits({}); }} className={`rounded-full px-3 py-1.5 ${basis === b ? "bg-surface font-semibold shadow-sm" : "text-ink-muted"}`}>{b} months</button>)}
          </div>
          <span className="text-xs text-ink-muted">{monthsUsed ? `${monthsUsed} month${monthsUsed === 1 ? "" : "s"} with reconciled lines, up to ${format(addMonths(end, -1), "MMMM")}` : "No reconciled lines in that window yet."}</span>
        </div>
        <p className="text-sm text-ink-muted">Each suggestion is that code's average month, rounded up to the next $10. Codes without a budget are ticked. Change any amount before setting. It only covers what's been reconciled, so more history makes it better.</p>
        <div className="overflow-hidden rounded-lg ring-1 ring-line">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-surface-raised/60 text-left text-xs text-ink-muted"><tr><th className="w-10 pl-4"><input type="checkbox" className="h-4 w-4 accent-primary" checked={rows.length > 0 && chosen.length === rows.length} onChange={(e) => setTicked(e.target.checked ? new Set(rows.map((r) => r.c.id)) : new Set())} /></th><th className="px-2 py-2 font-medium">Code</th><th className="px-2 py-2 text-right font-medium">Average month</th><th className="px-2 py-2 text-right font-medium">Budget now</th><th className="px-4 py-2 text-right font-medium">Set to</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.c.id} className="border-t border-line/60">
                <td className="pl-4"><input type="checkbox" className="h-4 w-4 accent-primary" checked={tick.has(r.c.id)} onChange={() => { const n = new Set(tick); if (n.has(r.c.id)) n.delete(r.c.id); else n.add(r.c.id); setTicked(n); }} /></td>
                <td className="px-2 py-2"><span className="num text-ink-muted">{r.c.code}</span> {r.c.name}<span className="ml-2 text-xs text-ink-muted">{groupOf(r.c)?.name}</span></td>
                <td className="num px-2 py-2 text-right">{money(Math.round(r.avg), { cents: true })}</td>
                <td className="num px-2 py-2 text-right text-ink-muted">{r.c.monthly_budget_cents ? money(r.c.monthly_budget_cents) : "—"}</td>
                <td className="px-4 py-2 text-right"><span className="inline-flex items-center rounded-md border border-line bg-bg px-2 focus-within:border-primary"><span className="text-ink-muted">$</span><input inputMode="decimal" value={value(r)} onChange={(e) => setEdits({ ...edits, [r.c.id]: e.target.value })} className="num w-20 bg-transparent px-1 py-1 text-right outline-none" /></span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-ink-muted">{totals.isLoading ? "Working it out…" : "Nothing to suggest yet. Reconcile a month or two first."}</td></tr>}</tbody>
          </table>
        </div>
        <ErrorText>{err || null}</ErrorText>
        <Button disabled={!chosen.length || busy} onClick={apply}><span className="flex items-center justify-center gap-2"><Sparkle className="h-4 w-4" />{busy ? "Setting…" : `Set ${chosen.length} budget${chosen.length === 1 ? "" : "s"} · ${money(total)} a month`}</span></Button>
      </div>
    </Sheet>
  );
}
